import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowRight, ChevronRight, Brain, Bot, BarChart3, Rocket,
  CheckCircle, Award, Users, Briefcase, Target, Zap, Lightbulb, Network,
  GraduationCap, Quote, Plus, Minus, X, Linkedin, Globe, Calendar,
  ShieldCheck, FileText, MessageCircle, Star, Clock, MapPin
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colors = {
  primary: '#B91C1C',
  primaryDark: '#7F1D1D',
  primaryLight: '#FEF2F2',
  text: '#0A0A0A',
  textSecondary: '#525252',
  textTertiary: '#A3A3A3',
  background: '#FAFAF9',
  surface: '#FFFFFF',
  subtle: '#F5F5F5',
  border: '#E5E5E5',
  success: '#059669',
  gold: '#B45309',
  goldSoft: '#FFFBEB',
};

const mediaUrl = (u) => (!u ? '' : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`));
const formatPrice = (p) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(p || 0);

const trackIcon = (id) => ({ ai: Brain, agentic: Bot, 'data-science': BarChart3 }[id] || Brain);

// Entrance wrapper — passthrough (hover/transition micro-interactions handled per-element)
const Reveal = ({ children, className = '', as: Tag = 'div' }) => (
  <Tag className={className}>{children}</Tag>
);

const SectionLabel = ({ children }) => (
  <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>{children}</p>
);

const FellowshipLandingPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTrack, setActiveTrack] = useState('ai');
  const [openFaq, setOpenFaq] = useState(0);
  const [showApply, setShowApply] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fellowship/content`);
      setData(res.data);
      if (res.data?.config?.tracks?.length) setActiveTrack(res.data.config.tracks[0].id);
    } catch (e) {
      console.error('Failed to load fellowship', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // SEO meta
  useEffect(() => {
    if (!data?.config?.seo) return;
    const { title, description } = data.config.seo;
    if (title) document.title = title;
    const setMeta = (name, content, prop = false) => {
      if (!content) return;
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let tag = document.querySelector(sel);
      if (!tag) { tag = document.createElement('meta'); tag.setAttribute(prop ? 'property' : 'name', name); document.head.appendChild(tag); }
      tag.setAttribute('content', content);
    };
    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', 'website', true);
    if (data.config.seo.og_image) setMeta('og:image', mediaUrl(data.config.seo.og_image), true);
    return () => { document.title = 'EvalentX'; };
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
          <Sparkles size={16} className="animate-pulse" style={{ color: colors.primary }} />
          <span className="text-sm font-bold tracking-wide uppercase">Loading Fellowship…</span>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const { config, mentors, guest_lecturers: lecturers, testimonials, partners, faqs } = data;
  const vis = config.sections_visibility || {};
  const hero = config.hero || {};
  const syllabus = config.syllabus || {};
  const immersion = config.startup_immersion || {};
  const cert = config.certificate || {};
  const pricing = config.pricing || {};
  const whatsapp = config.whatsapp || {};

  const benefitIcons = [Rocket, Brain, FileText, Network, Users, Briefcase, Target, Zap];
  const timelineIcons = [Calendar, GraduationCap, Lightbulb, Rocket, CheckCircle, Award];

  const waLink = whatsapp.enabled && whatsapp.number
    ? `https://wa.me/${whatsapp.number.replace(/\D/g, '')}?text=${encodeURIComponent(whatsapp.prefill_message || '')}`
    : null;

  return (
    <div className="min-h-screen" style={{ background: colors.background, color: colors.text }} data-testid="fellowship-page">
      {/* Admin banner */}
      {config.banner?.enabled && config.banner?.text && (
        <div className="text-center py-2 px-4 text-xs font-bold tracking-wide text-white" style={{ background: colors.primaryDark }} data-testid="fellowship-banner">
          {config.banner.link
            ? <a href={config.banner.link} className="hover:underline">{config.banner.text}</a>
            : config.banner.text}
        </div>
      )}

      {/* Sticky Header */}
      <header className="sticky top-0 z-40 border-b" style={{ background: colors.surface, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-7 h-7 flex items-center justify-center" style={{ background: colors.primary, borderRadius: '2px' }}>
              <Sparkles size={14} className="text-white" />
            </div>
            <span className="font-extrabold tracking-tight text-base" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX</span>
            <span className="hidden sm:inline-block px-1.5 py-0.5 text-[9px] font-bold tracking-[0.2em] uppercase border" style={{ borderColor: colors.border, color: colors.textTertiary, borderRadius: '1px' }}>Fellowship</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm font-semibold" style={{ color: colors.textSecondary }}>
            <a href="#about" className="hover:text-[#B91C1C] transition-colors">About</a>
            <a href="#tracks" className="hover:text-[#B91C1C] transition-colors">Tracks</a>
            <a href="#syllabus" className="hover:text-[#B91C1C] transition-colors">Syllabus</a>
            <a href="#immersion" className="hover:text-[#B91C1C] transition-colors">Immersion</a>
            <a href="#pricing" className="hover:text-[#B91C1C] transition-colors">Pricing</a>
          </nav>
          <button onClick={() => setShowApply(true)} data-testid="header-apply-btn"
            className="px-4 py-2 text-xs sm:text-sm font-bold tracking-wide uppercase text-white transition-transform hover:-translate-y-0.5"
            style={{ background: colors.primary, borderRadius: '2px' }}>
            {hero.primary_cta || 'Apply Now'}
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* subtle AI grid backdrop */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(${colors.border} 1px, transparent 1px), linear-gradient(90deg, ${colors.border} 1px, transparent 1px)`,
          backgroundSize: '48px 48px', maskImage: 'radial-gradient(ellipse 80% 60% at 70% 20%, black, transparent)', opacity: 0.5,
        }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative">
          <div className="grid lg:grid-cols-[1fr_420px] gap-10 lg:gap-14 items-center">
            <div className="min-w-0">
              <Reveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 border" style={{ borderColor: colors.border, background: colors.surface, borderRadius: '2px' }}>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: colors.success }} />
                  <span className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textSecondary }}>{hero.badge}</span>
                </div>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {hero.title_line1}<br /><span style={{ color: colors.primary }}>{hero.title_line2}</span>.
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="mt-6 text-base sm:text-lg leading-relaxed max-w-xl" style={{ color: colors.textSecondary }}>{hero.tagline}</p>
              </Reveal>
              <Reveal delay={240}>
                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <button onClick={() => setShowApply(true)} data-testid="hero-apply-btn"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5"
                    style={{ background: colors.primary, borderRadius: '2px' }}>
                    {hero.primary_cta || 'Apply Now'} <ArrowRight size={15} />
                  </button>
                  <a href="#syllabus"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold tracking-[0.1em] uppercase border transition-colors hover:bg-[#FAFAF9]"
                    style={{ borderColor: colors.text, color: colors.text, borderRadius: '2px' }}>
                    {hero.secondary_cta || 'View Curriculum'}
                  </a>
                </div>
              </Reveal>
              <Reveal delay={320}>
                <div className="mt-10 pt-6 border-t flex flex-wrap items-center gap-x-8 gap-y-3" style={{ borderColor: colors.border }}>
                  <HeroStat icon={Clock} value={hero.duration_label} label={hero.duration_sub} />
                  <div className="h-8 w-px" style={{ background: colors.border }} />
                  <HeroStat icon={Award} value={formatPrice(pricing.price)} label={pricing.note || 'Fellowship Fee'} />
                </div>
              </Reveal>
            </div>

            {/* Hero card */}
            <Reveal delay={200} className="relative hidden lg:block">
              <div className="relative z-10 bg-white border p-7" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4" style={{ color: colors.textTertiary }}>Program Snapshot</p>
                <div className="space-y-2.5">
                  <PhaseRow n="01" title="Intensive Learning" sub="1 Month · AI · Agentic AI · Data Science" />
                  <PhaseRow n="02" title="Startup Immersion" sub="1 Month · Remote / Physical" highlight />
                </div>
                <div className="mt-5 pt-5 border-t" style={{ borderColor: colors.border }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center" style={{ background: colors.primaryLight, borderRadius: '2px' }}>
                      <Rocket size={18} style={{ color: colors.primary }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{hero.immersion_highlight}</p>
                      <p className="text-[11px]" style={{ color: colors.textTertiary }}>Experiential, hands-on, real projects</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 w-12 h-12 -z-10" style={{ background: colors.primary, borderRadius: '2px' }} />
              <div className="absolute -bottom-3 -left-3 w-10 h-10 -z-10 border-4" style={{ borderColor: colors.text, background: colors.surface, borderRadius: '2px' }} />
            </Reveal>
          </div>
        </div>
      </section>

      {/* STARTUP IMMERSION PARTNER STRIP */}
      {mediaUrl(immersion.partner_logo_url) && (
        <section className="border-y" style={{ background: colors.surface, borderColor: colors.border }} data-testid="immersion-partner-strip">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3 text-center sm:text-left">
            <div className="flex items-center gap-2">
              <ShieldCheck size={14} style={{ color: colors.primary }} />
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>
                Startup Immersion Partner · Where you'll work
              </p>
            </div>
            <a href="#immersion" className="flex items-center gap-3 transition-transform hover:-translate-y-0.5">
              <img src={mediaUrl(immersion.partner_logo_url)} alt={immersion.partner_name || 'Startup Immersion Partner'}
                className="h-10 sm:h-11 object-contain" data-testid="immersion-partner-strip-logo" />
            </a>
          </div>
        </section>
      )}

      {/* ABOUT */}
      {vis.about && (
        <section id="about" className="border-y" style={{ background: colors.surface, borderColor: colors.border }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10">
              <Reveal>
                <div>
                  <SectionLabel>About the Fellowship</SectionLabel>
                  <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1 max-w-md" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{config.about?.heading}</h2>
                  <p className="text-sm sm:text-base leading-relaxed mt-4 max-w-md" style={{ color: colors.textSecondary }}>{config.about?.description}</p>
                </div>
              </Reveal>
              <div className="grid sm:grid-cols-2 gap-3">
                {(config.about?.pillars || []).map((p, i) => (
                  <Reveal key={i} delay={i * 60}>
                    <div className="border p-5 h-full transition-all hover:-translate-y-0.5" style={{ borderColor: colors.border, background: colors.background, borderRadius: '2px' }}>
                      <div className="w-9 h-9 flex items-center justify-center" style={{ background: colors.primaryLight, borderRadius: '2px' }}>
                        <CheckCircle size={16} style={{ color: colors.primary }} />
                      </div>
                      <h3 className="text-base font-extrabold mt-3 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{p.title}</h3>
                      <p className="text-xs leading-relaxed mt-1.5" style={{ color: colors.textSecondary }}>{p.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* TRACKS */}
      {vis.tracks && (config.tracks || []).length > 0 && (
        <section id="tracks" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Reveal>
            <div className="mb-8">
              <SectionLabel>Fellowship Tracks</SectionLabel>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Three frontiers. One fellowship.</h2>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-3 gap-3">
            {config.tracks.map((t, i) => {
              const Icon = trackIcon(t.id);
              return (
                <Reveal key={t.id} delay={i * 80}>
                  <div className="group border p-6 h-full transition-all bg-white" style={{ borderColor: colors.border, borderRadius: '2px' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(185,28,28,0.10)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div className="flex items-center justify-between">
                      <div className="w-11 h-11 flex items-center justify-center" style={{ background: colors.primaryLight, borderRadius: '2px' }}>
                        <Icon size={20} style={{ color: colors.primary }} />
                      </div>
                      <span className="text-[10px] font-bold tracking-[0.15em] uppercase tabular-nums" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>0{i + 1}</span>
                    </div>
                    <h3 className="text-xl font-extrabold mt-5 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{t.name}</h3>
                    <p className="text-sm leading-relaxed mt-2" style={{ color: colors.textSecondary }}>{t.overview}</p>
                    {(t.tools || []).length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {t.tools.map((tool, j) => (
                          <span key={j} className="px-2 py-0.5 text-[10px] font-bold tracking-wide border" style={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: '1px' }}>{tool}</span>
                        ))}
                      </div>
                    )}
                    {(t.outcomes || []).length > 0 && (
                      <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
                        <p className="text-[9px] font-bold tracking-[0.2em] uppercase mb-1.5" style={{ color: colors.textTertiary }}>Career Outcomes</p>
                        <p className="text-xs font-semibold" style={{ color: colors.text }}>{t.outcomes.join(' · ')}</p>
                      </div>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* SYLLABUS */}
      {vis.syllabus && Object.keys(syllabus).length > 0 && (
        <section id="syllabus" className="border-y" style={{ background: colors.surface, borderColor: colors.border }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <Reveal>
              <div className="mb-8">
                <SectionLabel>Curriculum</SectionLabel>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>What you'll master</h2>
              </div>
            </Reveal>
            <div className="flex flex-wrap gap-2 mb-8">
              {(config.tracks || []).map((t) => (
                <button key={t.id} onClick={() => setActiveTrack(t.id)} data-testid={`syllabus-tab-${t.id}`}
                  className="px-4 py-2 text-xs font-bold tracking-[0.1em] uppercase border transition-all"
                  style={{
                    borderColor: activeTrack === t.id ? colors.primary : colors.border,
                    background: activeTrack === t.id ? colors.primary : colors.surface,
                    color: activeTrack === t.id ? '#fff' : colors.textSecondary, borderRadius: '2px',
                  }}>
                  {t.name}
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {(syllabus[activeTrack] || []).map((mod, i) => (
                <Reveal key={`${activeTrack}-${i}`} delay={(i % 4) * 50}>
                  <div className="border p-4 h-full bg-white flex items-start gap-3 transition-colors hover:border-[#B91C1C]" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                    <span className="text-[11px] font-bold tabular-nums shrink-0 mt-0.5" style={{ color: colors.primary, fontFamily: "'IBM Plex Mono', monospace" }}>{String(i + 1).padStart(2, '0')}</span>
                    <span className="text-sm font-semibold leading-snug" style={{ color: colors.text }}>{mod}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STARTUP IMMERSION */}
      {vis.startup_immersion && (
        <section id="immersion" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="border relative overflow-hidden" style={{ background: colors.text, color: '#fff', borderColor: colors.text, borderRadius: '2px' }}>
            <div className="absolute top-0 right-0 bottom-0 w-1/3 hidden lg:block" style={{ background: colors.primary, clipPath: 'polygon(35% 0, 100% 0, 100% 100%, 0% 100%)', opacity: 0.9 }} />
            <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-8 p-8 sm:p-12">
              <Reveal>
                <div>
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/60">Phase 02 · Startup Immersion</p>
                  <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{immersion.heading}</h2>
                  <p className="text-sm sm:text-base mt-4 max-w-xl text-white/70">{immersion.description}</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-2.5 max-w-xl">
                    {(immersion.points || []).map((pt, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-white/85">
                        <CheckCircle size={15} style={{ color: '#FCA5A5' }} /> {pt}
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
              <Reveal delay={120} className="relative z-10 flex items-center justify-center">
                <div className="bg-white text-center px-6 py-7 w-full max-w-xs" style={{ borderRadius: '2px' }}>
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>{immersion.partner_tagline || 'Startup Immersion Partner'}</p>
                  {mediaUrl(immersion.partner_logo_url) ? (
                    <img src={mediaUrl(immersion.partner_logo_url)} alt={immersion.partner_name} className="h-16 mx-auto mt-4 object-contain" data-testid="immersion-partner-logo" />
                  ) : (
                    <p className="text-3xl font-extrabold tracking-tighter mt-4" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>{immersion.partner_name}</p>
                  )}
                  <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-bold tracking-wide uppercase" style={{ background: colors.primaryLight, color: colors.primary, borderRadius: '1px' }}>
                    <ShieldCheck size={12} /> Official Partner
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* MENTORS */}
      {vis.mentors && mentors.length > 0 && (
        <PeopleSection id="mentors" label="Mentors" title="Learn from practitioners" people={mentors} testid="mentor" />
      )}

      {/* GUEST LECTURES */}
      {vis.guest_lectures && lecturers.length > 0 && (
        <PeopleSection id="guest-lectures" label="Guest Lectures" title="Industry voices, live" people={lecturers} testid="lecturer" surface />
      )}

      {/* CERTIFICATE */}
      {vis.certificate && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <SectionLabel>Recognition</SectionLabel>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{cert.heading}</h2>
              <p className="text-sm mt-3" style={{ color: colors.textSecondary }}>{cert.description}</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4">
            <CertCard kind="certificate" title="Fellowship Completion Certificate" subtitle="Awarded on successful completion · LinkedIn-ready" previewUrl={mediaUrl(cert.certificate_preview_url)}
              evalentxLogo={mediaUrl(cert.evalentx_logo_url)} partnerLogo={mediaUrl(cert.partner_logo_url)} partnerName={immersion.partner_name} durationLabel={hero.duration_label ? `${hero.duration_label} program` : '2-month program'} testid="cert-completion" />
            <CertCard kind="letter" title="Letter of Appreciation" subtitle="Recognising your startup immersion contribution" previewUrl={mediaUrl(cert.letter_preview_url)}
              evalentxLogo={mediaUrl(cert.evalentx_logo_url)} partnerLogo={mediaUrl(cert.partner_logo_url)} partnerName={immersion.partner_name} testid="cert-letter" />
          </div>
        </section>
      )}

      {/* BENEFITS */}
      {vis.benefits && (config.benefits || []).length > 0 && (
        <section className="border-y" style={{ background: colors.surface, borderColor: colors.border }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <Reveal>
              <div className="mb-8">
                <SectionLabel>Why Join</SectionLabel>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Fellowship benefits</h2>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {config.benefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <Reveal key={i} delay={(i % 3) * 60}>
                    <div className="border p-5 h-full bg-white transition-colors hover:border-[#B91C1C]" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                      <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: colors.border, background: colors.primaryLight, borderRadius: '2px' }}>
                        <Icon size={18} style={{ color: colors.primary }} />
                      </div>
                      <h3 className="text-base font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{b.title}</h3>
                      <p className="text-sm leading-relaxed mt-1.5" style={{ color: colors.textSecondary }}>{b.description}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* TIMELINE */}
      {vis.timeline && (config.timeline || []).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Reveal>
            <div className="mb-10">
              <SectionLabel>The Journey</SectionLabel>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>From enrollment to certification</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {config.timeline.map((step, i) => {
              const Icon = timelineIcons[i % timelineIcons.length];
              return (
                <Reveal key={i} delay={i * 70}>
                  <div className="relative border p-5 h-full bg-white" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                    <span className="text-[10px] font-bold tabular-nums tracking-[0.2em]" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>STEP {String(i + 1).padStart(2, '0')}</span>
                    <div className="w-9 h-9 flex items-center justify-center mt-3" style={{ background: colors.primaryLight, borderRadius: '2px' }}>
                      <Icon size={16} style={{ color: colors.primary }} />
                    </div>
                    <h3 className="text-sm font-extrabold mt-3 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{step.title}</h3>
                    <p className="text-xs leading-relaxed mt-1" style={{ color: colors.textSecondary }}>{step.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* PRICING */}
      {vis.pricing && (
        <section id="pricing" className="border-y" style={{ background: colors.surface, borderColor: colors.border }}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <Reveal>
              <div className="text-center mb-8">
                <SectionLabel>Pricing</SectionLabel>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>One fee. Everything included.</h2>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="border relative overflow-hidden" style={{ borderColor: colors.text, background: colors.background, borderRadius: '2px' }}>
                <div className="absolute top-0 left-0 right-0 h-1" style={{ background: colors.primary }} />
                <div className="p-8 sm:p-10 text-center">
                  <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>EvalentX Fellowship</p>
                  <div className="mt-4 flex items-end justify-center gap-3">
                    {pricing.original_price > pricing.price && (
                      <span className="text-2xl font-bold tabular-nums line-through" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>{formatPrice(pricing.original_price)}</span>
                    )}
                    <span className="text-5xl sm:text-6xl font-extrabold tabular-nums tracking-tighter" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }} data-testid="pricing-amount">{formatPrice(pricing.price)}</span>
                  </div>
                  {pricing.note && <p className="text-sm mt-2" style={{ color: colors.textSecondary }}>{pricing.note}</p>}
                  <div className="mt-7 grid sm:grid-cols-2 gap-2 text-left max-w-md mx-auto">
                    {['1-Month Intensive Learning', '1-Month Startup Immersion', 'Hands-on AI Projects', 'Completion Certificate', 'Letter of Appreciation', 'Mentor Guidance'].map((f, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm" style={{ color: colors.textSecondary }}>
                        <CheckCircle size={14} style={{ color: colors.success }} /> {f}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setShowApply(true)} data-testid="pricing-apply-btn"
                    className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5"
                    style={{ background: colors.primary, borderRadius: '2px' }}>
                    Apply &amp; Enroll <ArrowRight size={15} />
                  </button>
                  <p className="text-[11px] mt-3" style={{ color: colors.textTertiary }}>Discount codes available at checkout</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* TESTIMONIALS */}
      {vis.testimonials && testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Reveal>
            <div className="mb-8">
              <SectionLabel>Testimonials</SectionLabel>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>What fellows say</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {testimonials.map((t, i) => (
              <Reveal key={t.id || i} delay={(i % 3) * 60}>
                <div className="border p-6 h-full bg-white relative" style={{ borderColor: colors.border, borderRadius: '2px' }} data-testid={`testimonial-${i}`}>
                  <Quote size={32} className="opacity-10 absolute top-4 right-4" />
                  {t.rating > 0 && (
                    <div className="flex gap-0.5 mb-3" style={{ color: '#F59E0B' }}>
                      {[...Array(Math.min(5, t.rating))].map((_, k) => <Star key={k} size={13} fill="currentColor" strokeWidth={0} />)}
                    </div>
                  )}
                  <p className="text-sm leading-relaxed" style={{ color: colors.text }}>"{t.text}"</p>
                  <div className="mt-5 pt-5 border-t flex items-center gap-3" style={{ borderColor: colors.border }}>
                    {mediaUrl(t.image_url)
                      ? <img src={mediaUrl(t.image_url)} alt={t.name} className="w-9 h-9 rounded-full object-cover" />
                      : <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-extrabold" style={{ background: colors.primaryLight, color: colors.primary }}>{t.name?.charAt(0)}</div>}
                    <div className="min-w-0">
                      <p className="text-sm font-bold truncate">{t.name}</p>
                      {(t.role || t.company) && <p className="text-[11px] truncate" style={{ color: colors.textTertiary }}>{[t.role, t.company].filter(Boolean).join(' · ')}</p>}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* PARTNERS */}
      {partners.length > 0 && (
        partners.some((p) => p.description || p.website) ? (
          <section className="border-t" style={{ background: colors.surface, borderColor: colors.border }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
              <p className="text-center text-[10px] font-bold tracking-[0.2em] uppercase mb-8" style={{ color: colors.textTertiary }}>Startup Partners</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {partners.map((p, i) => (
                  <div key={p.id || i} className="border p-5 flex flex-col transition-all hover:-translate-y-0.5" style={{ borderColor: colors.border, background: colors.background, borderRadius: '2px' }} data-testid={`partner-${i}`}>
                    <div className="h-12 flex items-center">
                      {mediaUrl(p.logo_url)
                        ? <img src={mediaUrl(p.logo_url)} alt={p.name} className="h-10 object-contain" />
                        : <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>{p.name}</span>}
                    </div>
                    {mediaUrl(p.logo_url) && <p className="text-sm font-extrabold mt-3 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>{p.name}</p>}
                    {p.description && <p className="text-xs leading-relaxed mt-1.5 flex-1" style={{ color: colors.textSecondary }}>{p.description}</p>}
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.1em] mt-3 hover:underline" style={{ color: colors.primary }}>
                        Visit <ArrowRight size={12} />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : (
          <section className="border-t" style={{ background: colors.surface, borderColor: colors.border }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
              <p className="text-center text-[10px] font-bold tracking-[0.2em] uppercase mb-6" style={{ color: colors.textTertiary }}>Startup Partners</p>
              <div className="flex flex-wrap items-center justify-center gap-8">
                {partners.map((p, i) => (
                  mediaUrl(p.logo_url)
                    ? <img key={p.id || i} src={mediaUrl(p.logo_url)} alt={p.name} className="h-10 object-contain grayscale hover:grayscale-0 transition-all" data-testid={`partner-${i}`} />
                    : <span key={p.id || i} className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.textSecondary }}>{p.name}</span>
                ))}
              </div>
            </div>
          </section>
        )
      )}

      {/* FAQ */}
      {vis.faq && faqs.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Reveal>
            <div className="mb-8">
              <SectionLabel>FAQ</SectionLabel>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Questions, answered</h2>
            </div>
          </Reveal>
          <div className="space-y-2">
            {faqs.map((f, i) => (
              <div key={f.id || i} className="border bg-white" style={{ borderColor: colors.border, borderRadius: '2px' }} data-testid={`faq-${i}`}>
                <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="w-full flex items-center justify-between gap-3 p-4 text-left">
                  <span className="text-sm font-bold" style={{ color: colors.text }}>{f.question}</span>
                  {openFaq === i ? <Minus size={16} style={{ color: colors.primary }} /> : <Plus size={16} style={{ color: colors.textTertiary }} />}
                </button>
                {openFaq === i && <div className="px-4 pb-4 text-sm leading-relaxed" style={{ color: colors.textSecondary }}>{f.answer}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="border p-8 sm:p-12 text-center relative overflow-hidden" style={{ background: colors.text, color: '#fff', borderColor: colors.text, borderRadius: '2px' }}>
          <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tighter" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Build your AI career in 2 months.</h2>
          <p className="text-sm sm:text-base mt-4 text-white/70 max-w-xl mx-auto">Intensive learning + real startup immersion with {immersion.partner_name}. Limited seats.</p>
          <button onClick={() => setShowApply(true)} data-testid="final-apply-btn"
            className="mt-7 inline-flex items-center gap-2 px-8 py-3.5 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5"
            style={{ background: colors.primary, borderRadius: '2px' }}>
            {hero.primary_cta || 'Apply Now'} · {formatPrice(pricing.price)} <ArrowRight size={15} />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t" style={{ background: colors.surface, borderColor: colors.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 flex items-center justify-center" style={{ background: colors.primary, borderRadius: '2px' }}><Sparkles size={11} className="text-white" /></div>
            <span className="font-extrabold text-sm tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX</span>
            <span className="px-1.5 py-0.5 text-[9px] font-bold tracking-[0.2em] uppercase border" style={{ borderColor: colors.border, color: colors.textTertiary, borderRadius: '1px' }}>Fellowship</span>
          </div>
          <p className="text-[11px]" style={{ color: colors.textTertiary }}>© 2026 EvalentX · AI · Agentic AI · Data Science · careers@evalentx.com</p>
        </div>
      </footer>

      {/* Floating Apply (mobile) */}
      <button onClick={() => setShowApply(true)} data-testid="floating-apply-btn"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 sm:hidden z-40 inline-flex items-center gap-2 px-6 py-3 text-sm font-bold tracking-[0.1em] uppercase text-white shadow-lg"
        style={{ background: colors.primary, borderRadius: '2px', boxShadow: '0 8px 24px rgba(185,28,28,0.35)' }}>
        {hero.primary_cta || 'Apply Now'} <ArrowRight size={14} />
      </button>

      {/* WhatsApp floating */}
      {waLink && (
        <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-cta"
          className="fixed bottom-20 sm:bottom-6 right-5 z-40 w-12 h-12 flex items-center justify-center text-white shadow-lg transition-transform hover:scale-110"
          style={{ background: '#25D366', borderRadius: '50%', boxShadow: '0 6px 20px rgba(37,211,102,0.4)' }} aria-label="WhatsApp inquiry">
          <MessageCircle size={22} />
        </a>
      )}

      {showApply && <ApplyModal config={config} onClose={() => setShowApply(false)} />}
    </div>
  );
};

const HeroStat = ({ icon: Icon, value, label }) => (
  <div className="flex items-center gap-2">
    <Icon size={15} style={{ color: colors.primary }} />
    <div>
      <p className="text-sm font-bold tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{value}</p>
      <p className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: colors.textTertiary }}>{label}</p>
    </div>
  </div>
);

const PhaseRow = ({ n, title, sub, highlight }) => (
  <div className="flex items-center gap-3 p-3 border" style={{ borderColor: highlight ? colors.primary : colors.border, background: highlight ? colors.primaryLight : colors.surface, borderRadius: '2px' }}>
    <span className="text-lg font-extrabold tabular-nums tracking-tighter" style={{ color: highlight ? colors.primary : colors.textTertiary, fontFamily: "'Space Grotesk', sans-serif" }}>{n}</span>
    <div className="min-w-0">
      <p className="text-sm font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{title}</p>
      <p className="text-[11px]" style={{ color: colors.textTertiary }}>{sub}</p>
    </div>
  </div>
);

const PeopleSection = ({ id, label, title, people, testid, surface }) => (
  <section id={id} className={surface ? 'border-y' : ''} style={surface ? { background: colors.surface, borderColor: colors.border } : {}}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
      <Reveal>
        <div className="mb-8">
          <SectionLabel>{label}</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{title}</h2>
        </div>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {people.map((m, i) => (
          <Reveal key={m.id || i} delay={(i % 4) * 60}>
            <div className="border p-5 h-full transition-all hover:-translate-y-0.5" style={{ borderColor: colors.border, background: surface ? colors.background : colors.surface, borderRadius: '2px' }} data-testid={`${testid}-${i}`}>
              {mediaUrl(m.image_url)
                ? <img src={mediaUrl(m.image_url)} alt={m.name} className="w-16 h-16 rounded-full object-cover" />
                : <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-extrabold" style={{ background: colors.primaryLight, color: colors.primary, fontFamily: "'Space Grotesk', sans-serif" }}>{m.name?.charAt(0)}</div>}
              <h3 className="text-base font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{m.name}</h3>
              {m.designation && <p className="text-xs font-semibold mt-0.5" style={{ color: colors.primary }}>{m.designation}</p>}
              {m.company && <p className="text-[11px]" style={{ color: colors.textTertiary }}>{m.company}</p>}
              {m.session_topic && (
                <p className="text-xs font-bold mt-2 leading-snug" style={{ color: colors.text }}>
                  <span className="block text-[9px] font-bold tracking-[0.18em] uppercase mb-0.5" style={{ color: colors.textTertiary }}>Session</span>
                  {m.session_topic}
                </p>
              )}
              {(m.bio || m.expertise) && <p className="text-xs leading-relaxed mt-2" style={{ color: colors.textSecondary }}>{m.bio || m.expertise}</p>}
              {m.skills && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {String(m.skills).split(',').map((s) => s.trim()).filter(Boolean).map((s, k) => (
                    <span key={k} className="text-[10px] font-semibold px-1.5 py-0.5 border" style={{ borderColor: colors.border, color: colors.textSecondary, borderRadius: '2px' }}>{s}</span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                {m.linkedin && <a href={m.linkedin} target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center border transition-colors hover:border-[#B91C1C]" style={{ borderColor: colors.border, borderRadius: '2px' }} aria-label="LinkedIn"><Linkedin size={13} style={{ color: colors.textSecondary }} /></a>}
                {m.website && <a href={m.website} target="_blank" rel="noopener noreferrer" className="w-7 h-7 flex items-center justify-center border transition-colors hover:border-[#B91C1C]" style={{ borderColor: colors.border, borderRadius: '2px' }} aria-label="Website"><Globe size={13} style={{ color: colors.textSecondary }} /></a>}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CertCard = ({ kind, title, subtitle, previewUrl, evalentxLogo, partnerLogo, partnerName, durationLabel, testid }) => {
  const isLetter = kind === 'letter';
  return (
    <Reveal>
      <div className="border p-3 sm:p-4 h-full bg-white transition-all" style={{ borderColor: colors.border, borderRadius: '2px' }} data-testid={testid}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(185,28,28,0.10)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}>
        <div className="relative aspect-[1.45/1] overflow-hidden" style={{ background: colors.surface, borderRadius: '2px', boxShadow: '0 12px 32px rgba(10,10,10,0.10)' }}>
          {previewUrl ? (
            <img src={previewUrl} alt={title} className="w-full h-full object-cover" />
          ) : isLetter ? (
            <LetterArtwork evalentxLogo={evalentxLogo} partnerLogo={partnerLogo} partnerName={partnerName} />
          ) : (
            <CertificateArtwork evalentxLogo={evalentxLogo} partnerLogo={partnerLogo} partnerName={partnerName} durationLabel={durationLabel} />
          )}
        </div>
        <h3 className="text-base font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{title}</h3>
        <p className="text-xs mt-1" style={{ color: colors.textSecondary }}>{subtitle}</p>
      </div>
    </Reveal>
  );
};

const BrandMark = ({ logo, small }) => (
  logo
    ? <img src={logo} alt="EvalentX" className={small ? 'h-5 object-contain' : 'h-7 object-contain'} />
    : (
      <div className="flex items-center gap-1.5">
        <div className={`${small ? 'w-4 h-4' : 'w-5 h-5'} flex items-center justify-center`} style={{ background: colors.primary, borderRadius: '2px' }}>
          <Sparkles size={small ? 9 : 11} className="text-white" />
        </div>
        <span className={`${small ? 'text-xs' : 'text-sm'} font-extrabold tracking-tight`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX</span>
      </div>
    )
);

// Premium seal / medallion
const Seal = ({ size = 56 }) => (
  <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
    <div className="absolute inset-0 rounded-full" style={{ border: `2px solid ${colors.primary}` }} />
    <div className="absolute rounded-full" style={{ inset: 4, border: `1px solid ${colors.primary}`, opacity: 0.5 }} />
    <div className="absolute rounded-full flex items-center justify-center" style={{ inset: 7, background: colors.primary }}>
      <Award size={size * 0.32} className="text-white" />
    </div>
  </div>
);

// Shared decorative frame + abstract background
const CertFrame = ({ children }) => (
  <>
    {/* abstract background */}
    <div className="absolute inset-0" style={{ background: 'radial-gradient(120% 90% at 50% -10%, #FEF2F2 0%, transparent 55%)' }} />
    <div className="absolute inset-0" style={{ backgroundImage: `linear-gradient(${colors.border} 1px, transparent 1px), linear-gradient(90deg, ${colors.border} 1px, transparent 1px)`, backgroundSize: '26px 26px', opacity: 0.25 }} />
    <Award className="absolute" style={{ right: '-8%', bottom: '-12%', width: '42%', height: '42%', color: colors.primary, opacity: 0.04 }} />
    {/* double frame */}
    <div className="absolute" style={{ inset: '9px', border: `1.5px solid ${colors.primary}` }} />
    <div className="absolute" style={{ inset: '13px', border: `1px solid ${colors.border}` }} />
    {/* corner accents */}
    {[['9px', '9px', 'top left'], ['9px', '9px', 'top right'], ['9px', '9px', 'bottom left'], ['9px', '9px', 'bottom right']].map((c, i) => (
      <div key={i} className="absolute" style={{
        [i < 2 ? 'top' : 'bottom']: c[0], [i % 2 === 0 ? 'left' : 'right']: c[1],
        width: '14px', height: '14px',
        borderTop: i < 2 ? `2px solid ${colors.primary}` : 'none',
        borderBottom: i >= 2 ? `2px solid ${colors.primary}` : 'none',
        borderLeft: i % 2 === 0 ? `2px solid ${colors.primary}` : 'none',
        borderRight: i % 2 === 1 ? `2px solid ${colors.primary}` : 'none',
      }} />
    ))}
    <div className="absolute" style={{ inset: '13px' }}>{children}</div>
  </>
);

const CertificateArtwork = ({ evalentxLogo, partnerLogo, partnerName, durationLabel }) => (
  <CertFrame>
    <div className="w-full h-full flex flex-col px-[5%] py-[4%]">
      {/* header logos */}
      <div className="flex items-center justify-between">
        <BrandMark logo={evalentxLogo} />
        {partnerLogo
          ? <img src={partnerLogo} alt={partnerName} className="h-6 object-contain" />
          : <span className="text-[9px] font-bold uppercase tracking-[0.15em]" style={{ color: colors.textTertiary }}>In partnership with<br /><span style={{ color: colors.textSecondary }}>{partnerName}</span></span>}
      </div>

      {/* body */}
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <p className="text-[9px] sm:text-[10px] font-bold tracking-[0.32em] uppercase" style={{ color: colors.primary }}>Certificate of Completion</p>
        <div className="w-8 h-px my-2" style={{ background: colors.primary }} />
        <p className="text-[10px]" style={{ color: colors.textTertiary }}>This is to certify that</p>
        <p className="text-2xl sm:text-3xl mt-1.5 px-4" style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', color: colors.text }}>[ Student Name ]</p>
        <div className="w-32 h-px mt-1.5" style={{ background: colors.border }} />
        <p className="text-[10px] sm:text-[11px] mt-3 max-w-[88%] leading-relaxed" style={{ color: colors.textSecondary }}>
          has successfully completed the <strong style={{ color: colors.text }}>EvalentX Fellowship</strong> in
          <strong style={{ color: colors.text }}> [ Fellowship Track ]</strong> — a {durationLabel} comprising a 1-month
          intensive program and a 1-month startup immersion with <strong style={{ color: colors.text }}>{partnerName}</strong>.
        </p>
      </div>

      {/* footer */}
      <div className="flex items-end justify-between">
        <div className="text-left">
          <p className="text-[7px] sm:text-[8px] font-bold uppercase tracking-[0.15em]" style={{ color: colors.textTertiary }}>Certificate ID</p>
          <p className="text-[9px] sm:text-[10px] font-bold tabular-nums" style={{ fontFamily: "'IBM Plex Mono', monospace", color: colors.text }}>EVX-FEL-XXXXXX</p>
          <p className="text-[7px] sm:text-[8px] font-bold uppercase tracking-[0.15em] mt-1.5" style={{ color: colors.textTertiary }}>Issued</p>
          <p className="text-[9px] sm:text-[10px] font-semibold" style={{ color: colors.text }}>[ DD MMM 2026 ]</p>
        </div>
        <Seal size={50} />
        <div className="text-center">
          <div className="w-20 sm:w-24 border-t pt-0.5" style={{ borderColor: colors.text }} />
          <p className="text-[8px] sm:text-[9px] font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Program Director</p>
          <p className="text-[7px] sm:text-[8px] uppercase tracking-[0.12em]" style={{ color: colors.textTertiary }}>EvalentX Fellowship</p>
        </div>
      </div>
    </div>
  </CertFrame>
);

const LetterArtwork = ({ evalentxLogo, partnerLogo, partnerName }) => (
  <CertFrame>
    <div className="w-full h-full flex flex-col px-[5%] py-[3.5%]">
      {/* letterhead */}
      <div className="flex items-center justify-between pb-2 border-b" style={{ borderColor: colors.border }}>
        <BrandMark logo={evalentxLogo} />
        {partnerLogo
          ? <img src={partnerLogo} alt={partnerName} className="h-5 object-contain" />
          : <span className="text-[8px] font-bold uppercase tracking-[0.15em]" style={{ color: colors.textTertiary }}>{partnerName}</span>}
      </div>

      <div className="flex items-center justify-between mt-2">
        <p className="text-[9px] sm:text-[10px] font-bold tracking-[0.28em] uppercase" style={{ color: colors.primary }}>Letter of Appreciation</p>
        <p className="text-[8px] sm:text-[9px]" style={{ color: colors.textTertiary }}>[ DD MMM 2026 ]</p>
      </div>

      <div className="flex-1 mt-2.5 text-left overflow-hidden">
        <p className="text-[10px] sm:text-[11px] font-semibold" style={{ color: colors.text }}>Dear [ Student Name ],</p>
        <p className="text-[9px] sm:text-[10px] mt-1.5 leading-relaxed" style={{ color: colors.textSecondary }}>
          It is with great pleasure that we recognise your outstanding contribution throughout the
          <strong style={{ color: colors.text }}> EvalentX Fellowship</strong>. Your dedication during the intensive learning
          program and your hands-on involvement in the startup immersion with
          <strong style={{ color: colors.text }}> {partnerName}</strong> reflected exceptional commitment, initiative, and professionalism.
        </p>
        <p className="text-[9px] sm:text-[10px] mt-1.5 leading-relaxed" style={{ color: colors.textSecondary }}>
          We deeply appreciate the projects you delivered and the impact you made during your immersion, and we wish you continued success in the journey ahead.
        </p>
      </div>

      {/* sign-off */}
      <div className="flex items-end justify-between pt-1">
        <div className="text-left">
          <p className="text-[9px] sm:text-[10px]" style={{ color: colors.textSecondary }}>Warm regards,</p>
          <div className="w-20 sm:w-24 border-t mt-3 pt-0.5" style={{ borderColor: colors.text }} />
          <p className="text-[8px] sm:text-[9px] font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Program Director</p>
          <p className="text-[7px] sm:text-[8px] uppercase tracking-[0.12em]" style={{ color: colors.textTertiary }}>EvalentX Fellowship</p>
        </div>
        <Seal size={44} />
      </div>
    </div>
  </CertFrame>
);

// ==================== Apply Modal ====================
const ApplyModal = ({ config, onClose }) => {
  const tracks = config.tracks || [];
  const pricing = config.pricing || {};
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', organization: '', track: tracks[0]?.name || '', mode: 'Remote' });
  const [coupon, setCoupon] = useState('');
  const [applied, setApplied] = useState(null);
  const [couponErr, setCouponErr] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const finalPrice = applied ? applied.final_price : pricing.price;

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!/^\+?[0-9\s-]{7,15}$/.test(form.phone)) e.phone = 'Valid phone required';
    if (!form.track) e.track = 'Select a track';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    setCouponErr('');
    try {
      const res = await axios.post(`${API}/fellowship/validate-coupon`, { coupon_code: coupon });
      setApplied(res.data);
    } catch (err) {
      setApplied(null);
      setCouponErr(err.response?.data?.detail || 'Invalid coupon');
    }
  };

  const submitApplication = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/fellowship/apply`, { ...form });
      setDone(true);
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || 'Submission failed. Try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  const payAndEnroll = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/fellowship/enroll`, { ...form, coupon_code: applied?.coupon_code || '' });
      if (res.data.payment_session_id) {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'production' });
        await cashfree.checkout({ paymentSessionId: res.data.payment_session_id, redirectTarget: '_self' });
      } else if (res.data.status === 'confirmed') {
        window.location.href = `/fellowship/payment-callback?order_id=${res.data.order_id}`;
      }
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || 'Enrollment failed. Try again.' });
      setSubmitting(false);
    }
  };

  const field = (name, label, type = 'text', placeholder = '') => (
    <div>
      <label className="block text-[11px] font-bold tracking-[0.1em] uppercase mb-1.5" style={{ color: colors.textSecondary }}>{label}</label>
      <input type={type} value={form[name]} onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))} placeholder={placeholder}
        data-testid={`apply-${name}`}
        className="w-full px-3 py-2.5 text-sm border outline-none transition-colors focus:border-[#B91C1C]"
        style={{ borderColor: errors[name] ? colors.primary : colors.border, borderRadius: '2px', background: colors.surface }} />
      {errors[name] && <p className="text-[11px] mt-1" style={{ color: colors.primary }}>{errors[name]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(10,10,10,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto border" style={{ borderColor: colors.border, borderRadius: '2px' }} onClick={(e) => e.stopPropagation()} data-testid="apply-modal">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b bg-white" style={{ borderColor: colors.border }}>
          <div>
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.primary }}>Apply</p>
            <h3 className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX Fellowship</h3>
          </div>
          <button onClick={onClose} data-testid="apply-close" className="w-8 h-8 flex items-center justify-center border hover:bg-[#FAFAF9]" style={{ borderColor: colors.border, borderRadius: '2px' }}><X size={16} /></button>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center" style={{ background: '#ECFDF5', borderRadius: '2px' }}><CheckCircle size={28} style={{ color: colors.success }} /></div>
            <h4 className="text-xl font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Application received!</h4>
            <p className="text-sm mt-2" style={{ color: colors.textSecondary }}>Our team will reach out shortly. You can also enroll now to secure your seat.</p>
            <div className="mt-6 flex flex-col gap-2">
              <button onClick={payAndEnroll} disabled={submitting} data-testid="apply-pay-after-submit"
                className="w-full px-6 py-3 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: colors.primary, borderRadius: '2px' }}>
                {submitting ? 'Processing…' : `Pay & Enroll · ${formatPrice(finalPrice)}`}
              </button>
              <button onClick={onClose} className="w-full px-6 py-3 text-sm font-bold tracking-[0.1em] uppercase border" style={{ borderColor: colors.border, borderRadius: '2px' }}>Done</button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {field('full_name', 'Full Name', 'text', 'Your full name')}
            <div className="grid sm:grid-cols-2 gap-3">
              {field('email', 'Email', 'email', 'you@email.com')}
              {field('phone', 'Phone Number', 'tel', '9999999999')}
            </div>
            {field('organization', 'College / Company', 'text', 'Optional')}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold tracking-[0.1em] uppercase mb-1.5" style={{ color: colors.textSecondary }}>Fellowship Track</label>
                <select value={form.track} onChange={(e) => setForm((f) => ({ ...f, track: e.target.value }))} data-testid="apply-track"
                  className="w-full px-3 py-2.5 text-sm border outline-none focus:border-[#B91C1C]" style={{ borderColor: colors.border, borderRadius: '2px', background: colors.surface }}>
                  {tracks.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold tracking-[0.1em] uppercase mb-1.5" style={{ color: colors.textSecondary }}>Preferred Mode</label>
                <select value={form.mode} onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value }))} data-testid="apply-mode"
                  className="w-full px-3 py-2.5 text-sm border outline-none focus:border-[#B91C1C]" style={{ borderColor: colors.border, borderRadius: '2px', background: colors.surface }}>
                  <option value="Remote">Remote</option>
                  <option value="Physical">Physical</option>
                </select>
              </div>
            </div>

            {/* Coupon */}
            <div>
              <label className="block text-[11px] font-bold tracking-[0.1em] uppercase mb-1.5" style={{ color: colors.textSecondary }}>Discount Code</label>
              <div className="flex gap-2">
                <input value={coupon} onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setApplied(null); setCouponErr(''); }} placeholder="Enter code" data-testid="apply-coupon"
                  className="flex-1 px-3 py-2.5 text-sm border outline-none focus:border-[#B91C1C]" style={{ borderColor: colors.border, borderRadius: '2px' }} />
                <button onClick={applyCoupon} data-testid="apply-coupon-btn" className="px-4 py-2.5 text-xs font-bold tracking-wide uppercase border transition-colors hover:bg-[#FAFAF9]" style={{ borderColor: colors.text, borderRadius: '2px' }}>Apply</button>
              </div>
              {couponErr && <p className="text-[11px] mt-1" style={{ color: colors.primary }}>{couponErr}</p>}
              {applied && <p className="text-[11px] mt-1" style={{ color: colors.success }}>{applied.message}</p>}
            </div>

            {/* Delivery format note */}
            <div className="border p-3.5" style={{ borderColor: colors.border, background: colors.primaryLight, borderRadius: '2px' }} data-testid="apply-format-note">
              <p className="text-[10px] font-bold tracking-[0.15em] uppercase mb-2" style={{ color: colors.primary }}>Program Delivery Format</p>
              <div className="space-y-1.5">
                <div className="flex items-start gap-2">
                  <Globe size={14} className="shrink-0 mt-0.5" style={{ color: colors.primary }} />
                  <p className="text-xs leading-relaxed" style={{ color: colors.textSecondary }}>The <strong style={{ color: colors.text }}>1-month training program</strong> will be conducted <strong style={{ color: colors.text }}>completely in online mode</strong>.</p>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin size={14} className="shrink-0 mt-0.5" style={{ color: colors.primary }} />
                  <p className="text-xs leading-relaxed" style={{ color: colors.textSecondary }}>The <strong style={{ color: colors.text }}>startup immersion phase</strong> may be conducted <strong style={{ color: colors.text }}>remotely or physically</strong>, depending on availability and startup requirements.</p>
                </div>
              </div>
            </div>

            {/* Price summary */}
            <div className="flex items-center justify-between p-3 border" style={{ borderColor: colors.border, background: colors.background, borderRadius: '2px' }}>
              <span className="text-xs font-bold tracking-wide uppercase" style={{ color: colors.textSecondary }}>Total</span>
              <div className="flex items-center gap-2">
                {applied && applied.discount_amount > 0 && <span className="text-sm line-through tabular-nums" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>{formatPrice(pricing.price)}</span>}
                <span className="text-xl font-extrabold tabular-nums" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{formatPrice(finalPrice)}</span>
              </div>
            </div>

            {errors.submit && <p className="text-sm" style={{ color: colors.primary }}>{errors.submit}</p>}

            <div className="flex flex-col gap-2 pt-1">
              <button onClick={payAndEnroll} disabled={submitting} data-testid="apply-pay-btn"
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                style={{ background: colors.primary, borderRadius: '2px' }}>
                {submitting ? 'Processing…' : `Pay & Enroll · ${formatPrice(finalPrice)}`}
              </button>
              <button onClick={submitApplication} disabled={submitting} data-testid="apply-submit-btn"
                className="w-full px-6 py-3 text-sm font-bold tracking-[0.1em] uppercase border transition-colors hover:bg-[#FAFAF9] disabled:opacity-60"
                style={{ borderColor: colors.text, borderRadius: '2px' }}>
                Submit Application Only
              </button>
            </div>
            <p className="text-[11px] text-center" style={{ color: colors.textTertiary }}>Prefer to talk first? Submit an application and we'll reach out.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FellowshipLandingPage;
